import {CellClassParams, ColDef, ICellRendererParams, ValueFormatterParams, ValueGetterParams} from 'ag-grid-community';
import * as CellRenderers from '../../../../../shared-modules/shared-grid/components-cell-renderers';
import * as CellEditors from '../../../../../shared-modules/shared-grid/components-cell-editors';
import {DataAdditionalIcons} from '../../data-additional-icons';
import {DataAdditionalNumbers} from '../../data-additional-numbers';
import {NGPReport} from '../../../ngp-reports/ngp-report';
import {DateUtils} from '../../../../utils-old/shared-utils-old/date-utils';
import {CELL_RENDERER_CURRENCY_NGP_REPORT} from 'src/app/shared-modules/shared-grid/models/js-cell-renderers';

import {TLocaleDateFormat} from '../../../../../features/settings-new/models';
import {
  getDepartmentDisplayName,
  getSubDepartmentDisplayName,
} from '../../../../../shared/shared-utils/stock/department-display.utils';

const costMissTooltip = 'Latest cost is not the same as last GRV price.';
const itemRecentlyCreatedTooltip = 'Item was recently created.';
const itemEditedTooltip = 'Item was marked as edited.';
const itemErrorTooltip = `Item has an error and can't be submitted`;

export const ngpReportGridColDefs: ColDef[] = [
  // IconsEnum ------------------------------
  {
    field: 'icons',
    headerName: '',
    hide: false,
    minWidth: 10,
    pinned: 'left',
    width: 50,
    resizable: true,
    lockPinned: true,
    lockVisible: true,
    lockPosition: true,
    suppressMovable: true,
    cellClass: 'grid-cell-icons',
    cellRenderer: CellRenderers.CELL_RENDERER_ICONS,
    cellRendererParams: {
      dataAdditional: {
        isCostMissIconVisible: true,
        costMissIconTooltip: costMissTooltip,
        isItemRecentlyCreatedIconVisible: true,
        itemRecentlyCreatedIconTooltip: itemRecentlyCreatedTooltip,
        isItemEditedIconVisible: true,
        itemEditedIconTooltip: itemEditedTooltip,
        isErrorIconVisible: true,
        itemErrorIconTooltip: itemErrorTooltip,
      } as DataAdditionalIcons,
    },
  },
  // General ----------------------------
  {
    field: 'stockId',
    headerName: 'Code',
    hide: false,
    pinned: 'left',
    width: 100,
    resizable: true,
    lockPinned: true,
    lockPosition: true,
    lockVisible: true,
    suppressMovable: true,
    editable: false,
    cellEditor: false,
    cellRenderer: CellRenderers.CELL_RENDERER_DISABLE_ITEM,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
    },
  },
  {
    field: 'desc',
    headerName: 'Description',
    hide: false,
    width: 350,
    lockPinned: true,
    pinned: 'left',
    resizable: true,
    lockVisible: true,
    lockPosition: true,
    suppressMovable: true,
    editable: false,
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CellRenderers.CELL_RENDERER_TEXT,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
    },
  },
  {
    field: 'onHoldCode',
    headerName: 'On Hold Code',
    hide: true,
    lockPinned: true,
    resizable: true,
    cellEditor: CellEditors.CELL_EDITOR_MULTI_SELECT_ON_HOLD,
    cellRenderer: CellRenderers.CELL_RENDERER_MULTI_SELECT,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<NGPReport>): boolean => {
        return params.data.disabled;
      },
    },
  },
  {
    field: 'onHand',
    headerName: 'On Hand',
    hide: false,
    lockPinned: true,
    type: ['alignRight'],
    cellEditor: false,
    cellRenderer: CELL_RENDERER_CURRENCY_NGP_REPORT,
  },
  // Cost -------------------------------
  {
    field: 'latestCost',
    headerName: 'Latest Cost',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellRenderer: CELL_RENDERER_CURRENCY_NGP_REPORT,
    cellEditor: false,
    cellRendererParams: {
      dataAdditional: {
        decimalPlaces: 2,
      } as DataAdditionalNumbers,
    },
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
    },
  },
  // Supplier ---------------------------
  {
    field: 'suppCode',
    headerName: 'Supplier Code',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    cellRenderer: CellRenderers.CELL_RENDERER_TEXT,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<NGPReport>): boolean => {
        return params.data.disabled;
      },
    },
  },
  {
    field: 'regularSuppCode',
    headerName: 'Supplier',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    cellRenderer: CellRenderers.CELL_RENDERER_TEXT,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<NGPReport>): boolean => {
        return params.data.disabled;
      },
    },
  },
  // Department -------------------------
  {
    field: 'dep',
    headerName: 'Department',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    cellRenderer: CellRenderers.CELL_RENDERER_DEPARTMENTS,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<NGPReport>): boolean => {
        return params.data.disabled;
      },
    },
  },
  {
    field: 'depName',
    headerName: 'Department Name',
    hide: true,
    lockPinned: true,
    resizable: true,
    sortable: false,
    cellEditor: false,
    cellRenderer: CellRenderers.CELL_RENDERER_DEPARTMENTS,
    valueGetter: (params: ValueGetterParams<NGPReport>): string => getDepartmentDisplayName(params.data?.dep),
    valueFormatter: (params: ValueFormatterParams<NGPReport>): string =>
      params.value ?? getDepartmentDisplayName(params.data?.dep),
    width: 175,
  },
  {
    field: 'subDep',
    headerName: 'Sub Department',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    cellRenderer: CellRenderers.CELL_RENDERER_DEPARTMENTS,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
    },
  },
  {
    field: 'subDepName',
    headerName: 'Sub Department Name',
    hide: true,
    lockPinned: true,
    resizable: true,
    sortable: false,
    cellEditor: false,
    cellRenderer: CellRenderers.CELL_RENDERER_DEPARTMENTS,
    valueGetter: (params: ValueGetterParams<NGPReport>): string => getSubDepartmentDisplayName(params.data?.subDep),
    valueFormatter: (params: ValueFormatterParams<NGPReport>): string =>
      params.value ?? getSubDepartmentDisplayName(params.data?.subDep),
    width: 175,
  },
  // Price ------------------------------
  {
    field: 'suppUsedLastPrice2',
    headerName: 'Prev Cost',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellRenderer: CELL_RENDERER_CURRENCY_NGP_REPORT,
    cellEditor: false,
    cellRendererParams: {
      dataAdditional: {
        decimalPlaces: 2,
      } as DataAdditionalNumbers,
    },
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<NGPReport>): boolean => {
        return params.data.disabled;
      },
    },
  },
  {
    field: 'sellPriIncl1',
    headerName: 'Retail Price',
    pinned: 'right',
    width: 100,
    hide: false,
    lockPinned: true,
    lockPosition: 'right',
    suppressMovable: true,
    lockVisible: true,
    type: ['alignRight'],
    resizable: true,
    cellRenderer: CELL_RENDERER_CURRENCY_NGP_REPORT,
    cellEditor: CellEditors.CELL_EDITOR_PRICE_NOMINAL_GP,
    cellRendererParams: {
      dataAdditional: {
        decimalPlaces: 2,
      } as DataAdditionalNumbers,
    },
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return +params.data.originalValue?.[pos]?.value !== +params.data[pos];
      },
    },
  },
  {
    field: 'prevGP',
    headerName: 'Previous GP %',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellRenderer: CELL_RENDERER_CURRENCY_NGP_REPORT,
    cellEditor: false,
    cellRendererParams: {
      dataAdditional: {
        decimalPlaces: 2,
      } as DataAdditionalNumbers,
    },
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
    },
  },
  {
    field: 'nominalGP',
    headerName: 'Current GP %',
    pinned: 'right',
    lockPinned: true,
    width: 100,
    hide: false,
    lockPosition: 'right',
    suppressMovable: true,
    lockVisible: true,
    type: ['alignRight'],
    resizable: true,
    cellRenderer: CELL_RENDERER_CURRENCY_NGP_REPORT,
    cellEditor: CellEditors.CELL_EDITOR_PRICE_NOMINAL_GP,
    cellRendererParams: {
      dataAdditional: {
        decimalPlaces: 2,
      } as DataAdditionalNumbers,
    },
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return +params.data.originalValue?.[pos]?.value !== +params.data[pos];
      },
    },
  },
  {
    field: 'diffGP',
    headerName: 'Δ GP %',
    width: 100,
    hide: false,
    lockVisible: true,
    pinned: 'right',
    lockPinned: true,
    lockPosition: 'right',
    suppressMovable: true,
    type: ['alignRight'],
    cellRenderer: CELL_RENDERER_CURRENCY_NGP_REPORT,
    cellEditor: false,
    cellRendererParams: {
      dataAdditional: {
        decimalPlaces: 2,
      } as DataAdditionalNumbers,
    },
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
    },
  },
  // GP ---------------------------------
  {
    field: 'suppUsedLastDate1',
    headerName: 'GRV Date',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    valueFormatter: (params: ValueFormatterParams<NGPReport, string | number | Date>): string => {
      const date = new Date(params.value);
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        return 'No Date Found';
      } else {
        const settings = params.context['dateSettings'] as TLocaleDateFormat;
        return DateUtils.dateToString(params.value as unknown as Date, settings);
      }
    },
    width: 175,
  },
  {
    field: 'gp1',
    headerName: 'GP % (Avg Cost)',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellRenderer: CELL_RENDERER_CURRENCY_NGP_REPORT,
    cellEditor: false,
    cellRendererParams: {
      dataAdditional: {
        decimalPlaces: 2,
      } as DataAdditionalNumbers,
    },
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<NGPReport>): boolean => {
        return params.data.disabled;
      },
    },
  },
  {
    field: 'recommendedGP',
    headerName: 'Target GP %',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRenderer: CELL_RENDERER_CURRENCY_NGP_REPORT,
    cellRendererParams: {dataAdditional: {decimalPlaces: 2} as DataAdditionalNumbers},
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<NGPReport>): boolean => {
        return params.data.disabled;
      },
    },
  },
  // UI ---------------------------------
  {
    field: 'lineColourCode',
    headerName: 'Line Colour',
    hide: true,
    lockPinned: true,
    resizable: true,
    cellEditor: false,
    cellRenderer: CellRenderers.CELL_RENDERER_LINE_COLOUR,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
    },
  },
  // Package ----------------------------
  {
    field: 'orderQuantity',
    headerName: 'Order Quantity',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellEditor: false,
    cellRenderer: CELL_RENDERER_CURRENCY_NGP_REPORT,
    cellRendererParams: {
      dataAdditional: {
        decimalPlaces: 2,
      } as DataAdditionalNumbers,
    },
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
    },
  },
  {
    field: 'packSize',
    headerName: 'Pack Size',
    hide: true,
    lockPinned: true,
    type: ['alignRight'],
    cellEditor: false,
    cellRenderer: CELL_RENDERER_CURRENCY_NGP_REPORT,
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<NGPReport>): boolean => {
        return params.data.disabled;
      },
    },
  },
  // Days -------------------------------
  {
    field: 'daysDiff',
    headerName: 'Δ Days',
    hide: false,
    lockVisible: true,
    lockPinned: true,
    cellEditor: false,
    type: ['alignRight'],
  },
  // Statistics -------------------------
  {
    field: 'unitsYear',
    headerName: 'Units 12 Months',
    cellEditor: false,
    hide: true,
    type: ['alignRight'],
    lockPinned: true,
    valueFormatter: (params: ValueFormatterParams<NGPReport>): string => {
      if (isNaN(params.data.unitsYear)) {
        return `0`;
      }
      return `${params.data.unitsYear}`;
    },
    resizable: true,
  },
  // Stock Take / Modified --------------
  {
    field: 'lastStockTake',
    headerName: 'Last Stock Take',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    valueFormatter: (params: ValueFormatterParams<NGPReport, string | number | Date>): string => {
      const date = new Date(params.value);
      if (isNaN(date.getTime())) {
        return 'No Date Found';
      } else {
        const settings = params.context['dateSettings'] as TLocaleDateFormat;
        return DateUtils.dateToString(params.value as unknown as Date, settings);
      }
    },
    width: 170,
  },
  {
    field: 'modified',
    headerName: 'Modified',
    hide: true,
    lockPinned: true,
    cellEditor: false,
    valueFormatter: (params: ValueFormatterParams<NGPReport, string | number | Date>): string => {
      const date = new Date(params.value);
      if (isNaN(date.getTime())) {
        return 'No Date Found';
      } else {
        const settings = params.context['dateSettings'] as TLocaleDateFormat;
        return DateUtils.dateToString(params.value as unknown as Date, settings);
      }
    },
    width: 170,
  },
  {
    field: 'orderQty',
    headerName: 'Order Quantity',
    hide: true,
    lockPinned: true,
    sortable: false,
    type: ['alignRight'],
    cellRenderer: (params: ICellRendererParams<NGPReport>): HTMLElement | string =>
      params.value == null ? 'undefined' : CELL_RENDERER_CURRENCY_NGP_REPORT(params),
    cellEditor: CellEditors.CELL_EDITOR_GENERAL,
    cellRendererParams: {dataAdditional: {decimalPlaces: 0} as DataAdditionalNumbers},
    cellClassRules: {
      ['cell-changed']: (params: CellClassParams<NGPReport>): boolean => {
        const pos = params.column.getColDef().field;
        return params.data.originalValue?.[pos]?.value !== params.data[pos];
      },
      ['cell-disabled']: (params: CellClassParams<NGPReport>): boolean => {
        return params.data.disabled;
      },
    },
    width: 100,
  },

// Selection Checkbox - Always Far Right ----------------------------
  {
    field: 'isSelected',
    headerName: '',
    hide: false,
    lockPosition: 'right',
    suppressMovable: true,
    pinned: 'right',
    lockVisible: true,
    maxWidth: 35,
    width: 35,
    minWidth: 35,
    filter: false,
    suppressHeaderMenuButton: true,
    checkboxSelection: true,
    resizable: false,
    suppressHeaderFilterButton: true,
  },
];
